import React, {useEffect, useState} from 'react';
import {
  useLocation,
  useParams,
} from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import useLoading from '../../hooks/useLoading';
import { useAuth } from '../../hooks/useAuth';
import useAppRouter from "../../hooks/useAppRouter";

import { roomMemberApi } from '../../apis/admin/room/roomMemberApi';


import { UpdateCustomerModal } from '../../components/pages/admin/RoomMemberEdit/UpdateCustomerModal';
import { UpdateSalesmanModal } from '../../components/pages/admin/RoomMemberEdit/UpdateSalesmanModal';
import { ChangeRoleModal } from '../../components/pages/admin/RoomMemberEdit/ChangeRoleModal';
import { ChangeStateModal } from '../../components/pages/admin/RoomMemberEdit/ChangeStateModal';
import { SelectSalesmanModal } from '../../components/pages/admin/RoomEdit/SelectSalesmanModal';

import {
  ChangeRoleModalParams,
  ChangeStateModalParams,
  Salesman,
} from '../../types/admin/room';

import {SalesmanType} from "../../enum/user/salesmanType";
import {ApiClientError} from "../../apis/errors";
import {logError} from "../../libs/utils/logError";
import {AddCustomerRoomMemberModal} from '../../components/pages/admin/RoomMember/AddCustomerRoomMemberModal';

interface RoomEditRouteParams {
    id: string;
}


export const RoomMemberEditPage: React.FC = () => {
  const { id } = useParams<RoomEditRouteParams>();
  const [completed, setCompleted] = React.useState(false);

  const { state: { currentUser } } = useAuth();

  const withLoading = useLoading();
  const location = useLocation();

  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();

  const [customerList, setCustomerList] = React.useState<any[]>([]);
  const [salesmanList, setSalesmanList] = React.useState<any[]>([]);
  const [selectedCustomerEmails, setSelectedCustomerEmails] = React.useState<string[]>([]);
  const [selectedSalesmanEmails, setSelectedSalesmanEmails] = React.useState<string[]>([]);

  const [selectUserId, setSelectUserId] = React.useState<number | undefined>();
  const [changeRoleModalParams, setChangeRoleModalParams] = React.useState<ChangeRoleModalParams | undefined>();
  const [changeStateModalParams, setChangeStateModalParams] = React.useState<ChangeStateModalParams | undefined>();

  const [showUpdateCustomerModal, setShowUpdateCustomerModal] = React.useState<boolean>(false);
  const [showUpdateSalesmanModal, setShowUpdateSalesmanModal] = React.useState<boolean>(false);
  const [showChangeRoleModal, setChangeRoleModal] = React.useState<boolean>(false);
  const [showChangeStateModal, setChangeStateModal] = React.useState<boolean>(false);
  const [showSelectSalesmanModal, setShowSelectSalesmanModal] = React.useState<boolean>(false);
  const [showAddCustomerRoomMemberModal, setShowAddCustomerRoomMemberModal] = React.useState<boolean>(false);

  const reloadMemberList = async () => {
    const response = await roomMemberApi.index(Number(id), {});
    setSalesmanList(response.sales);
    setCustomerList(response.customers);

    setSelectedSalesmanEmails(() => {
      return response.sales.map(salesman => {
        return salesman.email;
      });
    });

    setSelectedCustomerEmails(() => {
      return response.customers.map(customer => {
        return customer.email;
      });
    });
  }

  useEffect(() => {
    withLoading(async () => {
      try {
        await reloadMemberList();
      } catch(e) {
        await logError(
          location.pathname,
          '参加者一覧の読み込みに失敗しました。',
          e
        );

        goto('internalServerError');
        return;
      }

      setCompleted(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const onCustomerUpdateSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadMemberList();
    }
    setShowUpdateCustomerModal(false);
  };

  const onSalesmanUpdateSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadMemberList();
    }
    setShowUpdateSalesmanModal(false);
  };

  const onChangeRoleSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadMemberList();
    }
    setChangeRoleModal(false);
  };

  const onChangeStateSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadMemberList();
    }
    setChangeStateModal(false);
  };

  const onSelectSalesmanSubmitted = async (isChange: boolean, salesman: Salesman) => {
    if (isChange) {
      withLoading(async () => {
        setSelectedSalesmanEmails((prevState) => prevState.concat([salesman.email]));
        setApiClientError(null);

        try {
          if (salesman.userId === undefined) {
            return;
          }
          await roomMemberApi.addRoomMember({
            roomId: Number(id),
            userId: salesman.userId,
            roleId: 2,
          }, {});

          await reloadMemberList();
          setShowSelectSalesmanModal(false);

        } catch (e) {
          await logError(
            location.pathname,
            '副営業のROOM参加者への追加に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            setShowSelectSalesmanModal(false);
            return;
          }

          goto('internalServerError');
        }
      });
    } else {
      setShowSelectSalesmanModal(false);
    }
  };

  const onCustomerRoomMemberAdd = async () => {
    await reloadMemberList();
  };

  return (
    <>
      {showUpdateCustomerModal && (
        <UpdateCustomerModal
          {...{
            userId: selectUserId,
            onSubmitted: onCustomerUpdateSubmitted,
          }}
        />
      )}

      {showUpdateSalesmanModal && (
        <UpdateSalesmanModal
          {...{
            userId: selectUserId,
            onSubmitted: onSalesmanUpdateSubmitted,
          }}
        />
      )}

      {showChangeRoleModal && (
        <ChangeRoleModal
          {...{
            modalParams: changeRoleModalParams,
            onSubmitted: onChangeRoleSubmitted,
          }}
        />
      )}

      {showChangeStateModal && (
        <ChangeStateModal
          {...{
            modalParams: changeStateModalParams,
            onSubmitted: onChangeStateSubmitted,
          }}
        />
      )}

      {showSelectSalesmanModal && (
        <SelectSalesmanModal
          {...{
            selectedSalesmanEmail: selectedSalesmanEmails,
            onSubmitted: onSelectSalesmanSubmitted,
            setShowModal: setShowSelectSalesmanModal,
          }}
        />
      )}

      {showAddCustomerRoomMemberModal && (
        <AddCustomerRoomMemberModal
          roomId={Number(id)}
          selectedCustomerEmails={selectedCustomerEmails}
          onClosed={async () => setShowAddCustomerRoomMemberModal(false)}
          onAdded={onCustomerRoomMemberAdd}
        />
      )}

      <div className="flex flex-col bg-sub min-h-full px-8">
      {completed && (
      <>
        <div className="text-xl text-primary font-semibold py-6">
          参加者編集
        </div>
        <div className="text-danger text-xs mb-4">
          { apiClientError?.message }
        </div>

        <div className="mb-4 p-6">
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div className="grid justify-items-start">
              <button
                type="button"
                className="btn-primary col-end-2 mb-2 h-8 w-36"
                onClick={() => {
                  setShowAddCustomerRoomMemberModal(true);
                }}
              >
                顧客追加
              </button>
            </div>
            <div className="grid justify-items-start">
              <button
                type="button"
                className="btn-primary col-end-2 mb-2 h-8 w-36"
                onClick={() => {
                  setShowSelectSalesmanModal(true);
                }}
              >
                副営業担当追加
              </button>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4">
            {/* 顧客一覧 */}
            <div>
              { customerList.length > 0 && (
              <table className="table-auto border-separate w-full">
                <thead className="bg-primary text-white text-xs w-full">
                  <tr>
                    <th className="py-1 px-2 select-none">顧客名</th>
                    <th className="py-1 px-2 select-none">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white text-sm">
                  {customerList.map((item, index) => (
                  <tr key={`${index + 1}`}>
                    <td className="p-4 font-medium w-1/4 whitespace-normal break-all">
                      { item.roleId === 3 && (
                      <span>
                        <FontAwesomeIcon
                          icon={faStar}
                          className="text-gray-500"
                        />
                      </span>
                      )}
                      <span className="ml-2">{item.name}</span>
                    </td>
                    <td className="p-4 font-medium w-1/4 whitespace-normal">
                      <div className="grid grid-cols-1 items-center gap-1">
                        { !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setSelectUserId(item.id);
                            setShowUpdateCustomerModal(true);
                          }}
                        >
                          ユーザー情報変更
                        </button>
                        )}
                        { item.roleId !== 3 && !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setChangeStateModalParams({
                              roomId: Number(id),
                              userId: item.id,
                              userName: item.name,
                              disabledFlg: item.disabledFlg,
                            });
                            setChangeStateModal(true);
                          }}
                        >
                          { item.disabledFlg ? "再開" : "停止" }
                        </button>
                        )}
                        { item.roleId !== 3 && !item.disabledFlg && !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setChangeRoleModalParams({
                              roomId: Number(id),
                              userId: item.id,
                              userName: item.name,
                              mode: "owner",
                            });
                            setChangeRoleModal(true);
                          }}
                        >
                          オーナーに変更
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>

            {/* 営業一覧 */}
            <div>
              { salesmanList.length > 0 && (
              <table className="table-auto border-separate w-full">
                <thead className="bg-primary text-white text-xs w-full">
                  <tr>
                    <th className="py-1 px-2 select-none">営業担当名</th>
                    <th className="py-1 px-2 select-none">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white text-sm">
                  {salesmanList.map((item, index) => (
                  <tr key={`${index + 1}`}>
                    <td className="p-4 font-medium w-1/4 whitespace-normal break-all">
                      { item.salesPrimaryFlg && (
                      <span>
                        <FontAwesomeIcon
                          icon={faStar}
                          className="text-gray-500"
                        />
                      </span>
                      )}
                      <span className="ml-2">{item.name}</span>
                    </td>
                    <td className="p-4 font-medium w-1/4 whitespace-normal">
                      <div className="grid grid-cols-1 items-center gap-1">
                        { !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setSelectUserId(item.id);
                            setShowUpdateSalesmanModal(true);
                          }}
                          >
                          ユーザー情報変更
                        </button>
                        )}
                        { !item.salesPrimaryFlg && !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setChangeStateModalParams({
                              roomId: Number(id),
                              userId: item.id,
                              userName: item.name,
                              disabledFlg: item.disabledFlg,
                            });
                            setChangeStateModal(true);
                          }}
                          >
                          { item.disabledFlg ? "再開" : "停止" }
                        </button>
                        )}
                        { currentUser?.adminFlg === SalesmanType.ADMIN && !item.salesPrimaryFlg && !item.disabledFlg && !item.userDisabledFlg && (
                        <button
                          type="button"
                          className="btn-primary w-full"
                          onClick={() => {
                            setChangeRoleModalParams({
                              roomId: Number(id),
                              userId: item.id,
                              userName: item.name,
                              mode: "primarySales",
                            });
                            setChangeRoleModal(true);
                          }}
                          >
                          主担当に変更
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </div>

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4">
          <button
            className="btn-sub"
            type='button'
            onClick={() => goto('roomList')}
          >
            キャンセル
          </button>
        </div>
      </>
      )}
      </div>

    </>
  );
};
